import { BaseRecord, useCustom, useCustomMutation } from '@refinedev/core';
import { MutateOptions, UseQueryOptions } from '@tanstack/react-query';
import { ResourceField } from '@scalingworks/refine-react-admin';
import {
  AddFulfillmentToOrderResult,
  AdminOrderProcess,
  FulfillOrderInput,
  Order,
  OrderLineInput,
  OrderStatus,
  ProcessOrderInput,
  ProcessOrderResult,
  ProductList,
  UpdateOrderInfoInput,
  UpdateOrderOutletFulfillmentInput,
  UpdateOrderStatusInput,
} from '~/api';
import { productFields } from '../add-item';
import VariableOptions from 'gql-query-builder/build/VariableOptions';
import { TranslateFunction } from '~/config/types';
import { GQLClient } from '~/config/gql-client';
import { FulfillmentTrackingCodeLength, ManualFulfillmentCode } from '~/config/constant';
import { generateCode } from '~/resources/helpers';

type ProductInputOptions = {
  variables?: VariableOptions;
  queryOptions?: UseQueryOptions<any, any>;
};

const url = GQLClient.apiUrl;

export const useProducts = <T extends BaseRecord>(options?: ProductInputOptions) => {
  const hook = useCustom<T>({
    method: 'get',
    url,
    metaData: {
      fields: productFields as ResourceField<ProductList>[],
      operation: 'getProducts',
      variables: options?.variables,
    },
    queryOptions: options?.queryOptions,
  });

  return hook;
};

type OrderInputOptions = {
  id: string;
  queryOptions?: UseQueryOptions<any, any>;
};

export const useOrder = <T extends BaseRecord>(options: OrderInputOptions) => {
  const hook = useCustom<T>({
    method: 'get',
    url,
    metaData: {
      fields: [
        {
          lines: [
            'id',
            'quantity',
            'linePrice',
            'isAddOn',
            'linePriceWithAddon',
            'discountLinePriceWithAddon',
            { customFields: ['specialInstructions'] },
            {
              snapshot: [
                'productVariantId',
                'productVariantId',
                {
                  addons: [
                    'group',
                    {
                      modifiers: [
                        'modifierId',
                        { variants: ['name', 'price', 'quantity', 'variantId'] },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              productVariant: [
                'id',
                'name',
                'price',
                'productId',
                'currencyCode',
                { product: [{ assets: ['id', 'source'], featuredAsset: ['id', 'source'] }] },
              ],
            },
          ],
        },
      ] as ResourceField<Order>[],
      operation: 'order',
      variables: {
        id: {
          value: options.id,
          type: 'ID!',
        },
      },
    },
    queryOptions: options.queryOptions,
  });

  return hook;
};

type OrderHookOptions = {
  t: TranslateFunction;
  mutateOptions?: MutateOptions<any, any, any>;
};

type AddManualPaymentToOrderOptions = OrderHookOptions & {
  orderId: string;
  paymentMethodCode: string;
};

type AddFulfillmentToOrderOptions = OrderHookOptions & {
  orderId: string;
  method: string;
  lineInputs: OrderLineInput[];
  /**
   * @description auto update to preparing state first before creating fulfillment
   * @default false
   */
  autoMarkPreparing?: boolean;
};

export const useProcessOrder = () => {
  const { mutate: processOrder, isLoading } = useCustomMutation<ProcessOrderResult>();

  const addManualPaymentToOrder = (options: AddManualPaymentToOrderOptions) => {
    const { orderId, paymentMethodCode, t, mutateOptions = {} } = options;

    if (!paymentMethodCode) return;
    processOrder(
      {
        method: 'post',
        url,
        values: {},
        meta: {
          fields: ['key', 'value'] as ResourceField<ProcessOrderResult>[],
          operation: 'processOrder',
          variables: {
            input: {
              value: {
                orderId,
                types: [AdminOrderProcess.AddManualPayment],
                data: {
                  ADD_MANUAL_PAYMENT: {
                    method: paymentMethodCode,
                  },
                },
              } as ProcessOrderInput,
              type: 'ProcessOrderInput!',
            },
          },
        },
        // TODO: do for onSuccess because known error return as [200 OK]
        // successNotification: {
        //   message: t('order.show.paymentArranged', 'Payment Arranged'),
        //   type: 'success',
        // },
        errorNotification: {
          message: t('order.details.failedArrangePayment', 'Failed to arrange payment'),
          type: 'error',
        },
      },
      mutateOptions
    );
  };

  const addFulfillmentToOrder = (options: AddFulfillmentToOrderOptions) => {
    const {
      t,
      orderId,
      method,
      mutateOptions,
      lineInputs = [],
      autoMarkPreparing = false,
    } = options;

    if (autoMarkPreparing) {
      processOrder({
        url,
        method: 'post',
        metaData: {
          fields: ['id'],
          operation: 'updateOrderState',
          variables: {
            input: {
              type: 'UpdateOrderStatusInput!',
              value: {
                orderId,
                status: OrderStatus.Preparing,
              } as UpdateOrderStatusInput,
            },
          },
        },
        values: {},
      });
    }

    const trackingCode = generateCode({ length: FulfillmentTrackingCodeLength });
    processOrder(
      {
        method: 'post',
        url,
        values: {},
        meta: {
          fields: ['__typename'] as ResourceField<AddFulfillmentToOrderResult>[],
          operation: 'addFulfillmentToOrder',
          variables: {
            input: {
              value: {
                handler: {
                  arguments: [
                    { name: 'method', value: method },
                    { name: 'trackingCode', value: trackingCode },
                  ],
                  code: ManualFulfillmentCode,
                },
                lines: lineInputs,
              } as FulfillOrderInput,
              type: 'FulfillOrderInput!',
            },
          },
        },
        // TODO: do it in onSuccess
        // successNotification: {
        //   message: t('order.show.createdFulfillment'),
        //   type: 'success',
        // },
        errorNotification: {
          message: t('order.details.failedCreateFulfillment'),
          type: 'error',
        },
      },
      mutateOptions
    );
  };

  return { addManualPaymentToOrder, addFulfillmentToOrder, isLoading };
};

export const useUpdateOrderInfo = () => {
  const { mutate: updateOrder, isLoading } = useCustomMutation<Order>();

  const acceptOrder = async (orderId: string, options: OrderHookOptions) => {
  const { t, mutateOptions } = options;
    await updateOrder(
      {
        method: 'post',
        url,
        values: {},
        meta: {
          fields: ['id'] as ResourceField<Order>[],
          operation: 'updateOrderInfo',
          variables: {
            input: {
              value: {
                orderId,
                acceptedAt: new Date()
              } as UpdateOrderInfoInput,
              type: 'UpdateOrderInfoInput!',
            },
          },
        },
        successNotification: {
          message: t('order.details.orderUpdated', 'Order updated'),
          type: 'success',
        },
        errorNotification: {
          message: t('order.details.orderFailUpdate', 'Fail to update order'),
          type: 'error',
        },
      },
      mutateOptions
    );
  };

  const updateOrderRemark = async (orderId: string, remark: string, options: OrderHookOptions) => {
    const { t, mutateOptions } = options;
    await updateOrder(
      {
        method: 'post',
        url,
        values: {},
        meta: {
          fields: ['id'] as ResourceField<Order>[],
          operation: 'updateOrderInfo',
          variables: {
            input: {
              value: {
                orderId,
                remark,
              } as UpdateOrderInfoInput,
              type: 'UpdateOrderInfoInput!',
            },
          },
        },
        successNotification: {
          message: t('order.details.orderUpdated', 'Order updated'),
          type: 'success',
        },
        errorNotification: {
          message: t('order.details.orderFailUpdate', 'Fail to update order'),
          type: 'error',
        },
      },
      mutateOptions
    );
  };

  const updateOrderOutletFulfillment = async (
    orderId: string,
    input: UpdateOrderOutletFulfillmentInput,
    options: OrderHookOptions
  ) => {
    const { t, mutateOptions } = options;

    await updateOrder(
      {
        method: 'post',
        url,
        values: {},
        meta: {
          fields: ['id'] as ResourceField<Order>[],
          operation: 'updateOrderInfo',
          variables: {
            input: {
              value: {
                orderId,
                outletFulfillmentInput: input,
              } as UpdateOrderInfoInput,
              type: 'UpdateOrderInfoInput!',
            },
          },
        },
        successNotification: {
          message: t('order.details.orderUpdated', 'Order updated'),
          type: 'success',
        },
        errorNotification: {
          message: t('order.details.orderFailUpdate', 'Fail to update order'),
          type: 'error',
        },
      },
      mutateOptions
    );
  };

  return { acceptOrder, updateOrderRemark, updateOrderOutletFulfillment, isLoading };
};
